import React, { useState, useRef, useCallback, useEffect } from 'react';
import { auth, db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
    signInWithGoogle,
    signInWithApple,
    resolveRedirectResult,
    completeSocialSignup,
    suggestUsername,
    SOCIAL_APPLE_ENABLED,
} from './services/socialAuth';
import { doc, getDoc } from 'firebase/firestore';
import './Auth.css';
import {
    TOTAL_STEPS,
    RESERVED_USERNAMES,
    validateUsername,
    validateEmail,
    validatePassword,
    validateBirthday,
    authErrorMessage,
} from './utils/authHelpers';
import { hataAlani, hataMesaji } from './utils/hataAlani';






/* ─────────────────────────────────────────────────────────────
   ADIM ÇUBUĞU
───────────────────────────────────────────────────────────── */
function StepBar({ current, total }: { current: number; total: number }) {
    return (
        <div className="auth-step-bar">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    className={`auth-step-dot${i < current ? ' done' : i === current ? ' active' : ''}`}
                />
            ))}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────
   ANA BİLEŞEN
───────────────────────────────────────────────────────────── */
/**
 * 📏 ÇAĞRI YERİ SAYIMI: `<Auth` → 2 çağrı (App.tsx:872 propsuz, App.tsx:910
 * `socialCompletion` bayrağıyla).
 */
export default function Auth({ socialCompletion = false }: { socialCompletion?: boolean }) {
    const [isLogin, setIsLogin] = useState(true);
    const [error, setError]     = useState('');
    const [loading, setLoading] = useState(false);
    const [step, setStep]       = useState(0);

    /* Sosyal giriş (Google/Apple) */
    const [socialBusy, setSocialBusy]         = useState(false);
    const [socialError, setSocialError]       = useState('');
    const [socialUsername, setSocialUsername] = useState('');
    // 13 yaş kapısı sosyal yolda da toplanır (bkz. render'daki gerekçe).
    const [socialBirthday, setSocialBirthday] = useState('');

    // Redirect fallback dönüşünü tüket (popup engelli ortamlar / TÜM iOS).
    // Yeni-kullanıcı ele alma App'in socialCompletion guard'ı + authStore
    // üzerinden olur. BUG FIX (2026-06-30): resolveRedirectResult artık
    // hatayı yutmuyor (socialAuth.ts) — burada yakalayıp kullanıcıya
    // gösteriyoruz. Önceden iOS'ta (TÜM Google girişleri redirect kullanır)
    // başarısız bir giriş hiçbir mesaj göstermeden sessizce login ekranına
    // geri düşürüyordu ("giriş var ama girilmiyor" şikayeti).
    useEffect(() => {
        resolveRedirectResult().catch((e: any) => {
            const code = String(e?.code || '');
            if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                setSocialError(authErrorMessage(e) || 'Google ile giriş yapılamadı.');
            }
        });
    }, []);

    // Profil-tamamlama ekranında kullanıcı adını Google ad/email'inden ön-doldur.
    useEffect(() => {
        if (socialCompletion && !socialUsername) {
            const seed = auth.currentUser?.displayName || auth.currentUser?.email || '';
            setSocialUsername(suggestUsername(seed));
        }
    }, [socialCompletion]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGoogle = useCallback(async () => {
        setSocialError(''); setError(''); setSocialBusy(true);
        try {
            await signInWithGoogle();
            // pendingRedirect → sayfa yönlenir; aksi halde authStore + App guard
            // (yeni kullanıcı → username ekranı, mevcut → ana UI) devralır.
        } catch (e: any) {
            const code = String(e?.code || '');
            if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                setSocialError(authErrorMessage(e) || 'Google ile giriş yapılamadı.');
            }
        } finally {
            setSocialBusy(false);
        }
    }, []);

    const handleApple = useCallback(async () => {
        setSocialError(''); setError(''); setSocialBusy(true);
        try {
            await signInWithApple();
        } catch (e: any) {
            const code = String(e?.code || '');
            if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
                setSocialError(authErrorMessage(e) || 'Apple ile giriş yapılamadı.');
            }
        } finally {
            setSocialBusy(false);
        }
    }, []);

    const handleSocialUsernameSubmit = useCallback(async (e: any) => {
        e.preventDefault();
        const u = String(socialUsername || '').toLowerCase().trim();
        const err = validateUsername(u);
        if (err) { setSocialError(err); return; }
        // 13 yaş kapısı — sunucu zaten fail-closed reddediyor; buradaki kontrol
        // yalnız kullanıcıyı boş yere sunucuya göndermemek için (UX).
        const bdErr = validateBirthday(socialBirthday);
        if (bdErr) { setSocialError(bdErr); return; }
        setSocialBusy(true); setSocialError('');
        try {
            await completeSocialSignup({
                username: u,
                birthday: socialBirthday,
                displayName: auth.currentUser?.displayName || '',
                photoURL: auth.currentUser?.photoURL || '',
            });
            // Başarı: registerSocialUser users/{uid} yazar → authStore snapshot
            // profileMissing=false → App ana UI'a geçer (bu bileşen unmount olur).
        } catch (e2: any) {
            // Callable HttpsError mesajı kullanıcı-dostu Türkçe (username-taken vb.).
            setSocialError(e2?.message || authErrorMessage(e2) || 'Profil oluşturulamadı.');
            setSocialBusy(false);
        }
    }, [socialUsername, socialBirthday]);

    /* Login */
    const [email, setEmail]       = useState('');
    const [password, setPassword] = useState('');
    // 'idle' | 'sending' | 'sent' — şifre sıfırlama e-postası durumu
    const [resetStatus, setResetStatus] = useState('idle');

    /* Adım 0 */
    const [adSoyad, setAdSoyad]                 = useState('');
    const [kullaniciAdi, setKullaniciAdi]       = useState('');
    const [regEmail, setRegEmail]               = useState('');
    const [regPassword, setRegPassword]         = useState('');
    // Yorumdaki beş durum tipe çevrildi (aşağıdaki tüm `setUsernameStatus`
    // çağrıları ve 560-561'deki render karşılaştırmaları bu kümeden).
    const [usernameStatus, setUsernameStatus] = useState<
        null | 'checking' | 'ok' | 'taken' | 'invalid'
    >(null);
    const [usernameError, setUsernameError]     = useState('');
    const usernameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    /* Adım 1 — doğum tarihi (yasal 13+ kapı); başarıyla biten kayıt çağrısını tetikler */
    const [birthday, setBirthday]           = useState('');
    const [birthdayError, setBirthdayError] = useState('');
    // Instagram-parite sadeleştirme (2026-06-30): fotoğraf/ilgi-alanı/takip-önerisi
    // state'leri kaldırıldı — bu adımlar artık kayıt akışında değil, kayıt-sonrası
    // WelcomeWizard'da (components/WelcomeWizard.tsx, atlanabilir).

    /* ── Giriş ekranına geçince state temizle ── */
    const switchToLogin = () => {
        setIsLogin(true);
        setError('');
        setUsernameStatus(null);
        setUsernameError('');
    };

    /* ── Kayıt ekranına geçince state temizle ── */
    const switchToRegister = () => {
        setIsLogin(false);
        setStep(0);
        setError('');
        setUsernameStatus(null);
        setUsernameError('');
    };

    /* ════════════════════════════════════════════════════════
       KULLANICI ADI CANLI KONTROL
    ════════════════════════════════════════════════════════ */
    const handleUsernameChange = useCallback((rawVal: string) => {
        // Otomatik küçük harf + boşluk temizle
        const val = rawVal.toLowerCase().replace(/\s/g, '');
        setKullaniciAdi(val);
        setUsernameStatus(null);
        setUsernameError('');
        // `clearTimeout(null)` ile `clearTimeout(undefined)` ikisi de no-op.
        clearTimeout(usernameTimer.current ?? undefined);

        if (!val) return;

        // Anlık format kontrolü (API çağrısı yapmadan)
        const formatErr = validateUsername(val);
        if (formatErr) {
            setUsernameStatus('invalid');
            setUsernameError(formatErr);
            return;
        }

        // Rezerve kelime kontrolü
        if (RESERVED_USERNAMES.has(val)) {
            setUsernameStatus('invalid');
            setUsernameError('Bu kullanıcı adı rezerve edilmiştir.');
            return;
        }

        // 600ms debounce ile Firestore kontrolü
        setUsernameStatus('checking');
        usernameTimer.current = setTimeout(async () => {
            try {
                const snap = await getDoc(doc(db, 'usernames', val));
                setUsernameStatus(snap.exists() ? 'taken' : 'ok');
                setUsernameError(snap.exists() ? 'Bu kullanıcı adı alınmış.' : '');
            } catch {
                // Firestore erişim hatası — kayıt sırasında tekrar kontrol edilecek
                setUsernameStatus('ok');
                setUsernameError('');
            }
        }, 600);
    }, []);

    /* ════════════════════════════════════════════════════════
       GİRİŞ
    ════════════════════════════════════════════════════════ */
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Client-side validasyon
        const emailErr = validateEmail(email);
        if (emailErr) { setError(emailErr); return; }
        if (!password) { setError('Şifre girin.'); return; }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email.trim(), password);
            // Başarılı giriş — App.js auth state listener'ı devralır
        } catch (err) {
            setError(authErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    /* ════════════════════════════════════════════════════════
       ŞİFREMİ UNUTTUM
       Firebase resetPassword maili AuthActionHandler üzerinden
       hosted handler'a düşer; ek backend gerektirmez.
    ════════════════════════════════════════════════════════ */
    const handleForgotPassword = async () => {
        setError('');
        const emailErr = validateEmail(email);
        if (emailErr) { setError('Şifre sıfırlama için önce e-posta adresini yaz.'); return; }
        setResetStatus('sending');
        try {
            // Sıfırlama sonrası kullanıcı geldiği origin'e dönsün
            // (continueUrl'süz mail varsayılan domain'e düşürüyordu)
            await sendPasswordResetEmail(auth, email.trim(), {
                url: /^https:\/\/(mylasa\.app|mylasa-final\.(web\.app|firebaseapp\.com))$/.test(window.location.origin)
                    ? window.location.origin
                    : 'https://mylasa.app',
            });
            setResetStatus('sent');
        } catch (err) {
            // user-not-found'da da "gönderildi" göster — hesap enumeration'ı engeller
            if (hataAlani(err, 'code') === 'auth/user-not-found') {
                setResetStatus('sent');
            } else {
                setResetStatus('idle');
                setError(authErrorMessage(err));
            }
        }
    };

    /* ════════════════════════════════════════════════════════
       ADIM 0 → 1: Temel Bilgiler Doğrulama
    ════════════════════════════════════════════════════════ */
    const handleStep0 = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Ad Soyad — opsiyonel (IG paritesi). Boş bırakılırsa kayıt sırasında
        // kullanıcı adından türetilir (handleStep3). Doluysa minimum uzunluk kontrolü.
        const adSoyadTrim = adSoyad.trim();
        if (adSoyadTrim && adSoyadTrim.length < 2) {
            setError('Ad Soyad en az 2 karakter olmalıdır.'); return;
        }
        if (adSoyadTrim.length > 60) {
            setError('Ad Soyad çok uzun.'); return;
        }

        // Kullanıcı adı
        const unErr = validateUsername(kullaniciAdi);
        if (unErr) { setError(unErr); return; }
        if (usernameStatus === 'taken') { setError('Bu kullanıcı adı alınmış.'); return; }
        if (usernameStatus === 'invalid') { setError(usernameError || 'Geçersiz kullanıcı adı.'); return; }
        if (usernameStatus === 'checking') { setError('Kullanıcı adı kontrol ediliyor, lütfen bekleyin.'); return; }
        // usernameStatus null ise (Firestore'a erişim olmadı, kayıt sırasında transaction kontrol edecek)
        // Bu durumda devam et — transaction atomik olarak kontrol eder

        // E-posta
        const emailErr = validateEmail(regEmail);
        if (emailErr) { setError(emailErr); return; }

        // Şifre
        const passErr = validatePassword(regPassword);
        if (passErr) { setError(passErr); return; }

        setStep(1);
    };

    /* ════════════════════════════════════════════════════════
       ADIM 1 → FİREBASE KAYIT (doğum tarihi doğrulanınca direkt kayıt)
       Instagram-parite sadeleştirme (2026-06-30): fotoğraf/ilgi-alanı/
       takip-önerisi adımları kaldırıldı (WelcomeWizard kayıt-sonrası
       atlanabilir şekilde karşılıyor — components/WelcomeWizard.tsx).
       Doğum tarihi onaylanır onaylanmaz Cloud Function çağrılır; başarı
       durumunda auth state değişir, App.tsx Auth'u unmount eder ve
       WelcomeWizard otomatik devreye girer (ek "Adım 4" ekranı YOK —
       önceki sürümde bu ekran auth-state yarışı nedeniyle zaten hiç
       render olmuyordu, bkz. App.tsx:743 if(!user) return <Auth/>).
    ════════════════════════════════════════════════════════ */
    const handleStep1 = async (e: React.FormEvent) => {
        e.preventDefault();
        setBirthdayError('');
        // 🧭 TEK İSTEMCİ KOPYASI (`utils/authHelpers.validateBirthday`). Buradaki
        // satır-içi hesap sosyal yolla AYRIŞMAYA açıktı ve zaten ayrışmıştı:
        // sosyal yolda hiç kontrol yoktu. Ayrıca yerel-saat (`getFullYear`) ile
        // sunucunun UTC hesabı sınırda bir gün ayrışabiliyordu; ortak yardımcı
        // sunucuyla aynı UTC kuralını kullanır.
        const bdErr = validateBirthday(birthday);
        if (bdErr) { setBirthdayError(bdErr); return; }

        setError('');
        setLoading(true);

        try {
            const normalizedUsername = kullaniciAdi.toLowerCase().trim();
            const normalizedEmail    = regEmail.trim().toLowerCase();

            // ── 1. Son format doğrulaması ──────────────────────────
            const unErr = validateUsername(normalizedUsername);
            if (unErr) { setError(unErr); setLoading(false); return; }

            // ── 2. Cloud Function'ı çağır ─────────────────────────
            // Tüm işlemler sunucu tarafında atomik olarak yapılır:
            // Auth hesabı + Firestore profili + username rezervasyonu.
            // interests/photoBase64 kayıtta toplanmıyor — WelcomeWizard'a
            // ertelendi (her ikisi de backend'de zaten opsiyonel alan).
            const functionsInstance = getFunctions(undefined, 'europe-west3');
            const registerUserFn = httpsCallable(functionsInstance, 'registerUser');
            await registerUserFn({
                email:       normalizedEmail,
                password:    regPassword,
                username:    normalizedUsername,
                // Ad Soyad opsiyonel (IG paritesi) — boşsa kullanıcı adından türet
                // (server displayName zorunlu ister: functions/src/auth.ts:191).
                displayName: adSoyad.trim() || normalizedUsername,
                interests: [],
                birthday,
                photoBase64: null,
            });

            // ── 3. Firebase Auth ile giriş yap ────────────────────
            // Cloud Function Auth hesabını oluşturdu, şimdi client-side login.
            // Bu, authStore'daki onAuthStateChanged'i tetikler → App.tsx
            // Auth'u unmount edip ana uygulamayı (+ WelcomeWizard) render eder.
            await signInWithEmailAndPassword(auth, normalizedEmail, regPassword);

        } catch (err) {
            console.error(
                '[Auth] Kayıt hatası:',
                hataAlani(err, 'code'),
                hataMesaji(err),
                hataAlani(err, 'details'),
            );

            const code = hataAlani(err, 'code') || '';
            const msg  = hataMesaji(err) || '';

            if (code === 'functions/already-exists') {
                if (msg.toLowerCase().includes('e-posta') || msg.toLowerCase().includes('email')) {
                    setError('Bu e-posta adresi zaten kullanılıyor. Giriş yapmayı deneyin.');
                } else {
                    setError('Bu kullanıcı adı az önce başkası tarafından alındı. Lütfen farklı bir kullanıcı adı seçin.');
                    setUsernameStatus('taken');
                    setUsernameError('Bu kullanıcı adı alınmış.');
                    setStep(0);
                }
            } else if (code === 'functions/invalid-argument') {
                setError(msg || 'Girilen bilgileri kontrol edin.');
            } else if (code === 'functions/unauthenticated') {
                setError('Oturum hatası. Sayfayı yenileyip tekrar deneyin.');
            } else if (code === 'functions/internal') {
                setError('Sunucu hatası. Lütfen tekrar deneyin.');
            } else {
                setError(authErrorMessage(err) || 'Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.');
            }
        } finally {
            setLoading(false);
        }
    };

    /* ════════════════════════════════════════════════════════
       RENDER
    ════════════════════════════════════════════════════════ */

    /* ── Sosyal giriş butonları (login + kayıt Adım 0'da gösterilir) ── */
    const socialButtons = (
        <div className="auth-social">
            <div className="auth-social-divider"><span>veya</span></div>
            <button
                type="button"
                className="auth-social-btn auth-social-google pressable"
                onClick={handleGoogle}
                disabled={socialBusy}
            >
                <span className="auth-social-g" aria-hidden="true">G</span>
                Google ile devam et
            </button>
            {SOCIAL_APPLE_ENABLED && (
                <button
                    type="button"
                    className="auth-social-btn auth-social-apple pressable"
                    onClick={handleApple}
                    disabled={socialBusy}
                >
                    <span aria-hidden="true"></span>
                    Apple ile devam et
                </button>
            )}
            {socialError && <p className="auth-error" role="alert">{socialError}</p>}
        </div>
    );

    /* ── Sosyal giriş YENİ kullanıcısı: kullanıcı adı seç (profil tamamla) ── */
    if (socialCompletion) {
        return (
            <div className="auth-container">
                <link href="https://fonts.googleapis.com/css2?family=Cookie&display=swap" rel="stylesheet" />
                <div className="auth-form-wrapper auth-onboard">
                    <h1 className="auth-logo-text">Mylasa</h1>
                    <h2 className="auth-step-title">Profilini tamamla</h2>
                    <p className="auth-step-desc">Bir kullanıcı adı belirle ve doğum tarihini gir.</p>
                    <form onSubmit={handleSocialUsernameSubmit} className="auth-form" noValidate>
                        <input
                            type="text"
                            className="auth-input"
                            placeholder="kullaniciadi"
                            value={socialUsername}
                            onChange={e => setSocialUsername(e.target.value.toLowerCase())}
                            autoComplete="username"
                            autoFocus
                            required
                        />
                        {/*
                          🔴 DOĞUM TARİHİ — SOSYAL YOLDA HİÇ SORULMUYORDU.
                          E-posta kaydında ayrı bir adım olarak sorulan bu alan,
                          Google/Apple ile gelen kullanıcıda ne isteniyor ne de
                          sunucuya gönderiliyordu → 13 yaş kuralı bu yolda fiilen
                          YOKTU. Ayrıca legalContent.ts bu verinin toplandığını
                          kullanıcıya BEYAN ediyor. Sunucu artık fail-closed
                          reddediyor; alan zorunlu.
                        */}
                        <label className="auth-step-desc" htmlFor="social-birthday">
                            🎂 Doğum tarihin (profilinde gösterilmez)
                        </label>
                        <input
                            id="social-birthday"
                            type="date"
                            className="auth-input auth-date-input"
                            value={socialBirthday}
                            onChange={e => setSocialBirthday(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                            required
                        />
                        <button type="submit" className="auth-button" disabled={socialBusy}>
                            {socialBusy ? 'Oluşturuluyor…' : 'Devam Et'}
                        </button>
                        {socialError && <p className="auth-error" role="alert">{socialError}</p>}
                    </form>
                </div>
                <div className="auth-toggle-wrapper">
                    <p>
                        <button type="button" onClick={() => auth.signOut()} className="auth-toggle-button pressable">
                            Vazgeç (çıkış yap)
                        </button>
                    </p>
                </div>
            </div>
        );
    }

    /* ── Giriş Ekranı ── */
    if (isLogin) {
        return (
            <div className="auth-container">
                <link href="https://fonts.googleapis.com/css2?family=Cookie&display=swap" rel="stylesheet" />
                <div className="auth-form-wrapper">
                    <h1 className="auth-logo-text">Mylasa</h1>
                    <p className="auth-tagline">Rotanı paylaş, dünyayı keşfet.</p>
                    <form onSubmit={handleLogin} className="auth-form" noValidate>
                        <input
                            type="email"
                            className="auth-input"
                            placeholder="E-posta"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            autoComplete="email"
                            inputMode="email"
                            required
                        />
                        <input
                            type="password"
                            className="auth-input"
                            placeholder="Şifre"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                        <button type="submit" className="auth-button" disabled={loading}>
                            {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
                        </button>
                        {error && <p className="auth-error" role="alert">{error}</p>}
                        {resetStatus === 'sent' ? (
                            <p className="auth-reset-sent" role="status">
                                Şifre sıfırlama bağlantısı e-postana gönderildi. Gelen kutunu
                                (ve spam klasörünü) kontrol et.
                            </p>
                        ) : (
                            <button
                                type="button"
                                className="auth-forgot-button pressable"
                                onClick={handleForgotPassword}
                                disabled={resetStatus === 'sending'}
                            >
                                {resetStatus === 'sending' ? 'Gönderiliyor…' : 'Şifremi unuttum'}
                            </button>
                        )}
                    </form>
                    {socialButtons}
                </div>
                <div className="auth-toggle-wrapper">
                    <p>
                        Hesabın yok mu?{' '}
                        <button type="button" onClick={switchToRegister} className="auth-toggle-button pressable">
                            Kaydol
                        </button>
                    </p>
                </div>
            </div>
        );
    }

    /* ── Adım 0: Temel Bilgiler ── */
    if (step === 0) {
        return (
            <div className="auth-container">
                <link href="https://fonts.googleapis.com/css2?family=Cookie&display=swap" rel="stylesheet" />
                <div className="auth-form-wrapper auth-onboard">
                    <h1 className="auth-logo-text">Mylasa</h1>
                    <StepBar current={0} total={TOTAL_STEPS} />
                    <h2 className="auth-step-title">Hesabını oluştur</h2>
                    <p className="auth-step-desc">Mylasa'ya katılmak için birkaç bilgiye ihtiyacımız var.</p>
                    <form onSubmit={handleStep0} className="auth-form" noValidate>
                        <input
                            type="email"
                            className="auth-input"
                            placeholder="E-posta adresi"
                            value={regEmail}
                            onChange={e => setRegEmail(e.target.value)}
                            autoComplete="email"
                            inputMode="email"
                            required
                        />
                        <input
                            type="text"
                            className="auth-input"
                            placeholder="Ad Soyad (opsiyonel)"
                            value={adSoyad}
                            onChange={e => setAdSoyad(e.target.value)}
                            autoComplete="name"
                        />
                        <div className="auth-username-wrap">
                            <input
                                type="text"
                                className={`auth-input${
                                    usernameStatus === 'taken' || usernameStatus === 'invalid' ? ' input-error'
                                    : usernameStatus === 'ok' ? ' input-ok'
                                    : ''
                                }`}
                                placeholder="Kullanıcı adı (örn: recep.asik)"
                                value={kullaniciAdi}
                                onChange={e => handleUsernameChange(e.target.value)}
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                required
                            />
                            {usernameStatus === 'ok'       && <span className="auth-username-badge ok">✓</span>}
                            {(usernameStatus === 'taken' || usernameStatus === 'invalid') && <span className="auth-username-badge taken">✗</span>}
                            {usernameStatus === 'checking' && <span className="auth-username-badge checking">…</span>}
                        </div>
                        {(usernameStatus === 'taken' || usernameStatus === 'invalid') && usernameError && (
                            <p className="auth-field-error">{usernameError}</p>
                        )}
                        <p className="auth-field-hint">3-30 karakter, küçük harf, rakam, nokta ve alt çizgi kullanabilirsiniz.</p>
                        <input
                            type="password"
                            className="auth-input"
                            placeholder="Şifre (en az 6 karakter)"
                            value={regPassword}
                            onChange={e => setRegPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                        />
                        <button
                            type="submit"
                            className="auth-button"
                            disabled={loading || usernameStatus === 'taken' || usernameStatus === 'invalid' || usernameStatus === 'checking'}
                        >
                            Devam Et
                        </button>
                        {error && <p className="auth-error" role="alert">{error}</p>}
                    </form>
                    {socialButtons}
                </div>
                <div className="auth-toggle-wrapper">
                    <p>
                        Hesabın var mı?{' '}
                        <button type="button" onClick={switchToLogin} className="auth-toggle-button pressable">
                            Giriş Yap
                        </button>
                    </p>
                </div>
            </div>
        );
    }

    /* ── Adım 1: Doğum Tarihi ── */
    if (step === 1) {
        return (
            <div className="auth-container">
                <div className="auth-form-wrapper auth-onboard">
                    <h1 className="auth-logo-text">Mylasa</h1>
                    <StepBar current={1} total={TOTAL_STEPS} />
                    <div className="auth-step-icon">🎂</div>
                    <h2 className="auth-step-title">Doğum tarihin nedir?</h2>
                    <p className="auth-step-desc">Bu bilgiyi profilinde göstermeyeceğiz. Platforma güvenli erişim için gereklidir.</p>
                    <form onSubmit={handleStep1} className="auth-form">
                        <input
                            type="date"
                            className="auth-input auth-date-input"
                            value={birthday}
                            onChange={e => setBirthday(e.target.value)}
                            max={new Date().toISOString().split('T')[0]}
                            required
                        />
                        {birthdayError && <p className="auth-error" role="alert">{birthdayError}</p>}
                        {error && <p className="auth-error" role="alert">{error}</p>}
                        <button type="submit" className="auth-button" disabled={loading}>
                            {loading ? 'Hesap oluşturuluyor…' : 'Hesabı Oluştur'}
                        </button>
                    </form>
                    <button type="button" className="auth-back-btn pressable" onClick={() => setStep(0)} disabled={loading}>← Geri</button>
                </div>
            </div>
        );
    }

    return null;
}
